/**
 * Login Widget - Embeddable Bundle Entry Point
 *
 * The header's account control on woodsidebible.org, in place of the stock
 * `<mpp-user-login>` dropdown. That element stays on the page, hidden, so
 * MPWidgets.js still catches the OAuth callback and stores the token every
 * MP widget reads; this one renders the face and name and, on tap, a drawer
 * with the household, giving, anything owed, groups, the full profile and
 * Sign out. Same bundle family as the profile page (shared API client,
 * stores and cards), its own file.
 *
 * Embed (inline, inside the header's menu item; one per menu item, the
 * theme renders two):
 *   <span data-login-root
 *         data-profile-url="https://woodsidebible.org/profile/"></span>
 *   <script src="https://widgets.woodsidebible.org/profile/login.js"></script>
 */

import React from 'react';
import { LoginWidget } from '../src/login/LoginWidget';
import { attr } from './params';
import { ShadowWidget } from './shell';
import { definedSettings } from '@/lib/settings';

function num(raw: string | undefined): number | undefined {
  const n = Number(raw);
  return Number.isInteger(n) && n > 0 ? n : undefined;
}

interface LoginConfig {
  apiBaseUrl?: string;
  containerId?: string;
  mock?: boolean;
  profileUrl?: string;
  groupFinderUrl?: string;
  giveUrl?: string;
  payUrl?: string;
  manageGivingUrl?: string;
  seeJesusCampaignId?: number;
  blur?: boolean;
}

declare global {
  interface Window {
    LOGIN_WIDGET_CONFIG?: LoginConfig;
    LoginWidget?: ShadowWidget;
  }
}

const DEFAULT_CONTAINER_ID = 'login-root';

function blurWanted(config: LoginConfig): boolean {
  if (config.blur !== undefined) return config.blur;
  const q = new URLSearchParams(window.location.search).get('blur');
  if (q !== null) return q === '1' || q === 'true' || q === '';
  const el = document.getElementById(config.containerId || DEFAULT_CONTAINER_ID);
  const d = el?.dataset.blur;
  return d === '1' || d === 'true' || d === '';
}

function createLoginWidget(config: LoginConfig, containerId: string): ShadowWidget {
  const apiBaseUrl = config.apiBaseUrl || 'https://api.woodsidebible.org';
  const blur = blurWanted(config);
  return new ShadowWidget({
    containerIds: [containerId],
    overlayHostId: 'login-overlay',
    fontId: 'profile-widget-fonts',
    scrollLockFlag: '__LOGIN_WIDGET_SCROLL_LOCK_PREVENTED__',
    label: 'Login Widget',
    inline: true,
    hostClass: blur ? 'blur-pii' : undefined,
    render: (container) => (
      <LoginWidget
        apiBaseUrl={apiBaseUrl}
        mock={config.mock ?? false}
        // Precedence: explicit config > data-* attributes > MP's CUSTOMWIDGETS
        // settings (fetched at boot) > the defaults in lib/settings.
        profileUrl={config.profileUrl ?? attr(container.dataset.profileUrl)}
        settings={definedSettings({
          groupFinderUrl: config.groupFinderUrl ?? attr(container.dataset.groupFinderUrl),
          giveUrl: config.giveUrl ?? attr(container.dataset.giveUrl),
          payUrl: config.payUrl ?? attr(container.dataset.payUrl),
          manageGivingUrl: config.manageGivingUrl ?? attr(container.dataset.manageGivingUrl),
          seeJesusCampaignId:
            config.seeJesusCampaignId ?? num(container.dataset.seeJesusCampaignId),
        })}
        blurPii={blur}
        variant={(container.dataset.loginVariant as LoginVariant | undefined) ?? 'trigger'}
      />
    ),
  });
}

/**
 * Every mount point on the page. The site header renders the login menu item
 * more than once (the desktop nav, the sticky header, the off-canvas menu),
 * so `[data-login-root]` marks each and `#login-root` (or the configured id)
 * still works alone. Elements without an id get one, since the shell mounts
 * by id.
 *
 * On woodsidebible.org the theme's menu scripts rebuild the login menu item
 * once at load and drop the span the PHP put there, while anything added
 * afterwards stays. So when no mount is found, one is made inside every
 * `.login-target` item (the class the theme gives the login menu items).
 */
const LOGIN_ITEM_SELECTOR = '.login-target';
const OFF_CANVAS_SELECTOR = '#slide-out-widget-area, .slide-out-widget-area';

export type LoginVariant = 'trigger' | 'menu-identity' | 'menu-footer';

/**
 * Inside the theme's off-canvas menu (the hamburger) the widget is not a
 * trigger and a drawer: the person sits at the TOP of the menu as an identity
 * block with a link to the full profile, and Change password and Sign out sit
 * at the BOTTOM, under the menu. So a login item in there is moved to the
 * front of its list and marked `menu-identity`, and one `menu-footer` mount
 * is added per off-canvas area, before its bottom meta block.
 */
function placeInOffCanvas(item: HTMLElement, span: HTMLElement) {
  const area = item.closest<HTMLElement>(OFF_CANVAS_SELECTOR);
  if (!area) return;
  // One identity per menu, and it goes above everything: the theme renders a
  // short mobile-only list (Jesus, Give) before the main one, and hides the
  // login item it put in that list. The first login item in the area is the
  // one to keep: moved to the front of the first list, shown; the rest go.
  const items = Array.from(area.querySelectorAll<HTMLElement>(LOGIN_ITEM_SELECTOR));
  if (items[0] !== item) {
    item.remove();
    span.remove();
    return;
  }
  span.dataset.loginVariant = 'menu-identity';
  const scope = item.closest<HTMLElement>('.left-side, .inner') ?? area;
  const first = scope.querySelector<HTMLElement>('ul.menu') ?? item.parentElement;
  if (first && first.firstElementChild !== item) first.prepend(item);
  item.style.display = 'block';
  if (!area.querySelector('[data-login-variant="menu-footer"]')) {
    const footer = document.createElement('div');
    footer.setAttribute('data-login-root', '');
    footer.dataset.loginVariant = 'menu-footer';
    const inner = area.querySelector<HTMLElement>('.inner') ?? area;
    const meta = inner.querySelector<HTMLElement>('.bottom-meta-wrap');
    if (meta && meta.parentElement) meta.parentElement.insertBefore(footer, meta);
    else inner.appendChild(footer);
  }
}

function mountPoints(config: LoginConfig): string[] {
  const ids: string[] = [];
  const seen = new Set<Element>();
  const configured = document.getElementById(config.containerId || DEFAULT_CONTAINER_ID);
  let all = [configured, ...Array.from(document.querySelectorAll('[data-login-root]'))].filter(
    (el): el is HTMLElement => el instanceof HTMLElement
  );
  if (all.length === 0) {
    all = Array.from(document.querySelectorAll<HTMLElement>(LOGIN_ITEM_SELECTOR))
      .map((item) => {
        const span = document.createElement('span');
        span.setAttribute('data-login-root', '');
        // Without an explicit profileUrl the widget resolves it (MP's ProfileBaseURL, then the default).
        if (config.profileUrl) span.dataset.profileUrl = config.profileUrl;
        item.appendChild(span);
        if (item.closest(OFF_CANVAS_SELECTOR)) placeInOffCanvas(item, span);
        return span;
      })
      .filter((span) => span.isConnected);
    // The footer mounts placeInOffCanvas added
    all = all.concat(
      Array.from(document.querySelectorAll<HTMLElement>('[data-login-variant="menu-footer"]'))
    );
  }
  all.forEach((el, i) => {
    if (seen.has(el)) return;
    seen.add(el);
    if (!el.id) el.id = `${DEFAULT_CONTAINER_ID}-${i + 1}`;
    ids.push(el.id);
  });
  return ids;
}

if (typeof window !== 'undefined') {
  const config: LoginConfig = {
    apiBaseUrl: process.env.CHURCH_HUB_API_URL || 'https://api.woodsidebible.org',
    ...(window.LOGIN_WIDGET_CONFIG || {}),
  };
  window.LOGIN_WIDGET_CONFIG = config;
  if (!window.LoginWidget) {
    const boot = (): boolean => {
      const ids = mountPoints(config);
      if (ids.length === 0) return false;
      const widgets = ids.map((id) => {
        const widget = createLoginWidget(config, id);
        widget.init();
        return widget;
      });
      window.LoginWidget = widgets[0];
      return true;
    };
    const start = () => {
      if (boot()) return;
      // The menu items may not exist yet (or were just rebuilt): once more after load.
      const later = () => {
        if (!window.LoginWidget && !boot()) {
          console.error(
            `Login Widget: no container found (expected #${DEFAULT_CONTAINER_ID}, [data-login-root] or ${LOGIN_ITEM_SELECTOR})`
          );
        }
      };
      if (document.readyState === 'complete') later();
      else window.addEventListener('load', later, { once: true });
    };
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
    else start();
  }
}

export default ShadowWidget;
